// Cookie management utilities
import { initializeAnalytics, removeAnalytics } from './analytics';

export const COOKIE_PREFERENCES_KEY = 'cookie_preferences';
export const COOKIE_CONSENT_KEY = 'cookie_consent_accepted';

// Default cookie preferences
export const DEFAULT_PREFERENCES = {
  essential: true, // Always true, cannot be disabled
  analytics: false,
  marketing: false,
  personalization: false
};

/**
 * Get the current cookie preferences
 * @returns {Object} Current cookie preferences
 */
export function getCookiePreferences() {
  try {
    // First check for migration
    const migrated = migrateLegacyConsent();
    if (migrated) {
      return migrated;
    }
    
    const saved = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (saved) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.warn('Error reading cookie preferences:', e);
  }
  return DEFAULT_PREFERENCES;
}

/**
 * Check if cookies are consented to
 * @returns {boolean} Whether cookies have been consented to
 */
export function hasCookieConsent() {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === 'true';
}

/**
 * Check if a specific category of cookies is allowed
 * @param {string} category - Cookie category (analytics, marketing, personalization)
 * @returns {boolean} Whether the category is allowed
 */
export function isCookieCategoryAllowed(category) {
  if (category === 'essential') return true; // Essential cookies always allowed
  
  const preferences = getCookiePreferences();
  return preferences[category] === true;
}

/**
 * Initialize Google Analytics if analytics cookies are allowed
 */
export async function initializeAnalyticsFeature() {
  if (isCookieCategoryAllowed('analytics')) {
    try {
      await initializeAnalytics();
      console.log('Analytics cookies allowed - GA initialized');
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  } else {
    console.log('Analytics cookies not allowed - GA blocked');
    removeAnalytics();
  }
}

/**
 * Initialize marketing/advertising cookies if allowed
 */
export function initializeMarketing() {
  if (isCookieCategoryAllowed('marketing')) {
    console.log('Marketing cookies allowed - Ad tracking would be initialized');
    
    // Example marketing cookie initialization:
    /*
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
    */
  } else {
    console.log('Marketing cookies not allowed - Ad tracking blocked');
  }
}

/**
 * Initialize personalization features if allowed
 */
export function initializePersonalization() {
  if (isCookieCategoryAllowed('personalization')) {
    console.log('Personalization cookies allowed - User preferences would be saved');
    // Initialize personalization features like theme preferences, language settings, etc.
  } else {
    console.log('Personalization cookies not allowed - Default settings used');
  }
}

/**
 * Initialize all cookie-dependent features based on user preferences
 */
export async function initializeCookieFeatures() {
  if (hasCookieConsent()) {
    await initializeAnalyticsFeature();
    initializeMarketing();
    initializePersonalization();
  }
}

/**
 * Clear all non-essential cookies when user changes preferences
 */
export function clearNonEssentialCookies() {
  const preferences = getCookiePreferences();
  
  // Clear analytics cookies if not allowed
  if (!preferences.analytics) {
    // Clear Google Analytics cookies
    const analyticsKeys = ['_ga', '_ga_', '_gid', '_gat'];
    analyticsKeys.forEach(key => {
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      // Also clear for different domains if needed
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    });
  }
  
  // Clear marketing cookies if not allowed
  if (!preferences.marketing) {
    // Clear common marketing cookies
    const marketingKeys = ['_fbp', '_fbc', 'fr'];
    marketingKeys.forEach(key => {
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    });
  }
  
  // Clear personalization cookies if not allowed (except essential preferences)
  if (!preferences.personalization) {
    // Clear non-essential personalization cookies
    const personalizationKeys = ['user_theme', 'user_language'];
    personalizationKeys.forEach(key => {
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }
}

/**
 * Migrate existing users who have consent but no explicit preferences
 * This handles the case where users accepted cookies before the preference system was properly implemented
 */
export function migrateLegacyConsent() {
  const hasConsent = localStorage.getItem(COOKIE_CONSENT_KEY) === 'true';
  const hasPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  
  // If user has consent but no preferences, assume they accepted all
  if (hasConsent && !hasPreferences) {
    const allAcceptedPreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAcceptedPreferences));
    console.log('Migrated legacy consent to full preferences');
    return allAcceptedPreferences;
  }
  
  return null;
}

/**
 * Dispatch a custom event to notify components of cookie preference changes
 * This works within the same tab, unlike localStorage storage events
 */
export function dispatchCookiePreferenceChange() {
  const event = new CustomEvent('cookiePreferencesChanged', {
    detail: getCookiePreferences()
  });
  window.dispatchEvent(event);
}
